// LogbookTimeline - Chronologische Tagebuch-Ansicht mit Datum-Gruppierung

import React, { useMemo } from 'react';
import { Calendar, MapPin, Clock } from 'lucide-react';
import { NoteCard } from '../notes';
import type { PlaceNote } from '../../types';
import { Timestamp } from '@firebase/firestore';
import { getMoodByValue } from '../../constants/moods';

interface LogbookTimelineProps {
  notes: PlaceNote[];
  onEdit: (note: PlaceNote) => void;
  onDelete: (noteId: string) => void;
  onNavigateToPlace?: (placeId: string) => void;
}

interface DateGroup {
  label: string;
  notes: PlaceNote[];
  isToday?: boolean;
  isYesterday?: boolean;
}

function getDateFromTimestamp(date: Timestamp | Date | undefined): Date {
  if (!date) return new Date(0);
  if (date instanceof Timestamp) return date.toDate();
  return new Date(date);
}

function formatDateHeader(date: Date): string {
  return date.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function isSameDay(d1: Date, d2: Date): boolean {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function isWithinDays(date: Date, days: number): boolean {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  return diff < days * 24 * 60 * 60 * 1000;
}

export function LogbookTimeline({
  notes,
  onEdit,
  onDelete,
  onNavigateToPlace,
}: LogbookTimelineProps) {
  // Gruppiere Notizen nach Zeiträumen
  const dateGroups = useMemo(() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: Map<string, DateGroup> = new Map();

    // Sortiere nach Datum (neueste zuerst)
    const sortedNotes = [...notes].sort((a, b) => {
      const aDate = getDateFromTimestamp(a.visitDate || a.createdAt);
      const bDate = getDateFromTimestamp(b.visitDate || b.createdAt);
      return bDate.getTime() - aDate.getTime();
    });

    sortedNotes.forEach((note) => {
      const date = getDateFromTimestamp(note.visitDate || note.createdAt);
      let groupKey: string;
      let groupLabel: string;
      let isToday = false;
      let isYesterday = false;

      if (isSameDay(date, today)) {
        groupKey = 'today';
        groupLabel = 'Heute';
        isToday = true;
      } else if (isSameDay(date, yesterday)) {
        groupKey = 'yesterday';
        groupLabel = 'Gestern';
        isYesterday = true;
      } else if (isWithinDays(date, 7)) {
        groupKey = 'thisWeek';
        groupLabel = 'Diese Woche';
      } else if (isWithinDays(date, 30)) {
        groupKey = 'thisMonth';
        groupLabel = 'Diesen Monat';
      } else if (isWithinDays(date, 365)) {
        groupKey = 'thisYear';
        groupLabel = 'Dieses Jahr';
      } else {
        groupKey = `year_${date.getFullYear()}`;
        groupLabel = `${date.getFullYear()}`;
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          label: groupLabel,
          notes: [],
          isToday,
          isYesterday,
        });
      }
      groups.get(groupKey)!.notes.push(note);
    });

    return Array.from(groups.values());
  }, [notes]);

  if (notes.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Noch keine Einträge im Tagebuch</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {dateGroups.map((group, groupIndex) => (
        <div key={group.label} className="relative">
          {/* Timeline-Linie */}
          <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gradient-to-b from-indigo-300 to-transparent" />

          {/* Gruppen-Header */}
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`
                w-8 h-8 rounded-full flex items-center justify-center z-10
                ${group.isToday
                  ? 'bg-indigo-500 text-white'
                  : group.isYesterday
                    ? 'bg-indigo-400 text-white'
                    : 'bg-gray-200 text-gray-600'
                }
              `}
            >
              <Calendar className="w-4 h-4" />
            </div>
            <h3
              className={`
                text-lg font-semibold
                ${group.isToday ? 'text-indigo-600' : 'text-gray-700'}
              `}
            >
              {group.label}
            </h3>
            <span className="text-sm text-gray-400">
              {group.notes.length} {group.notes.length === 1 ? 'Eintrag' : 'Einträge'}
            </span>
          </div>

          {/* Einträge */}
          <div className="space-y-4 pl-11">
            {group.notes.map((note) => (
              <TimelineEntry
                key={note.id}
                note={note}
                onEdit={() => onEdit(note)}
                onDelete={() => onDelete(note.id)}
                onNavigateToPlace={onNavigateToPlace}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Kompakter Timeline-Eintrag
interface TimelineEntryProps {
  note: PlaceNote;
  onEdit: () => void;
  onDelete: () => void;
  onNavigateToPlace?: (placeId: string) => void;
}

function TimelineEntry({ note, onEdit, onDelete, onNavigateToPlace }: TimelineEntryProps) {
  const date = getDateFromTimestamp(note.visitDate || note.createdAt);
  const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  // Erstes Mood für Badge
  const primaryMood = note.moods?.[0] ? getMoodByValue(note.moods[0]) :
                      note.mood ? getMoodByValue(note.mood) : null;

  // Erstes Foto als Vorschau
  const previewPhoto = note.photoMetadata?.[0]?.thumbnailUrl || note.photoMetadata?.[0]?.url;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Header mit Zeit und Ort */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <Clock className="w-4 h-4" />
            <span>{timeStr}</span>
          </div>
          <button
            onClick={() => onNavigateToPlace?.(note.placeId)}
            className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            <MapPin className="w-4 h-4" />
            <span className="font-medium">{note.placeName}</span>
          </button>
        </div>
        {primaryMood && (
          <span className="text-lg" title={primaryMood.label}>
            {primaryMood.emoji}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex gap-4">
          {/* Foto-Vorschau */}
          {previewPhoto && (
            <div className="flex-shrink-0">
              <img
                src={previewPhoto}
                alt=""
                className="w-20 h-20 object-cover rounded-lg"
              />
              {note.photoMetadata && note.photoMetadata.length > 1 && (
                <span className="text-xs text-gray-500 mt-1 block text-center">
                  +{note.photoMetadata.length - 1} mehr
                </span>
              )}
            </div>
          )}

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="text-gray-700 line-clamp-3">{note.text}</p>

            {/* Moods */}
            {note.moods && note.moods.length > 1 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {note.moods.slice(1).map((moodValue) => {
                  const mood = getMoodByValue(moodValue);
                  if (!mood) return null;
                  return (
                    <span
                      key={moodValue}
                      className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
                      title={mood.label}
                    >
                      {mood.emoji} {mood.label}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Bewertung */}
            {note.personalRating && (
              <div className="flex items-center gap-1 mt-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={i}
                    className={`text-sm ${i < note.personalRating! ? 'text-amber-400' : 'text-gray-200'}`}
                  >
                    ★
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-gray-100">
          <button
            onClick={onEdit}
            className="text-sm text-gray-500 hover:text-indigo-600 transition-colors"
          >
            Bearbeiten
          </button>
          <button
            onClick={onDelete}
            className="text-sm text-gray-500 hover:text-red-600 transition-colors"
          >
            Löschen
          </button>
        </div>
      </div>
    </div>
  );
}

export default LogbookTimeline;
